import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getTimeUntilDeadline } from '../../hooks/useSuggestedCourses';

export default function CountdownBadge({ startDate, className = '' }) {
  const { t } = useTranslation('planner');
  const label = t('countdown.offerEndsIn');
  const [timeLeft, setTimeLeft] = useState(() => getTimeUntilDeadline(startDate));

  useEffect(() => {
    // Update every second - when startDate changes, effect re-runs and interval picks it up
    // Initial value is already set by useState, so no immediate sync needed
    const interval = setInterval(() => {
      setTimeLeft(getTimeUntilDeadline(startDate));
    }, 1000);

    return () => clearInterval(interval);
  }, [startDate]);

  // Don't render if expired or no date
  if (timeLeft === null || timeLeft <= 0) return null;

  const totalSeconds = Math.floor(timeLeft / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  // const seconds = totalSeconds % 60; // Available for future use

  // Determine urgency level for colors
  const isUrgent = days === 0 && hours < 6;
  const isCritical = days === 0 && hours === 0 && minutes < 30;

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium transition-colors ${
        isCritical
          ? 'bg-red-100 dark:bg-transparent dark:border dark:border-red-500 text-red-700 dark:text-red-400'
          : isUrgent
            ? 'bg-orange-100 dark:bg-transparent dark:border dark:border-orange-500 text-orange-700 dark:text-orange-400'
            : 'bg-red-100 dark:bg-transparent dark:border dark:border-red-500 text-red-700 dark:text-red-400'
      } ${className}`}
    >
      {/* Label */}
      {label && <span className="opacity-80">{label}</span>}

      {/* Time display */}
      <span className="tabular-nums">
        {days > 0 ? (
          // Show days, hours, minutes when > 24h
          <>{days}d {hours}h {minutes}m</>
        ) : hours > 0 ? (
          // Show hours and minutes when < 24h
          <>{hours}h {minutes}m</>
        ) : (
          // Show just minutes when < 1h
          <>{minutes}m</>
        )}
      </span>
    </span>
  );
}
